import { useId } from 'react';
import { useFormContext, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { DURATION_UNITS } from '../schemas/duration';
import type { WorkflowFormInput } from '../schemas/workflow-form-schema';

export interface DurationFieldProps {
  label: string;
  hint?: string;
  valueName: FieldPath<WorkflowFormInput>;
  unitName: FieldPath<WorkflowFormInput>;
  disabled?: boolean;
}

/** A number and a unit on one line; the mapper turns them into the API's calendar minutes. */
export function DurationField({ label, hint, valueName, unitName, disabled }: DurationFieldProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<WorkflowFormInput>();
  const labelId = useId();
  const hintId = useId();

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
      <Label id={labelId}>{label}</Label>
      <FormField
        control={form.control}
        name={valueName}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start gap-2">
              <FormControl>
                <Input
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  value={typeof field.value === 'string' ? field.value : ''}
                  onChange={field.onChange}
                  inputMode="numeric"
                  className="w-28"
                  aria-label={label}
                  aria-describedby={hint ? hintId : undefined}
                  disabled={disabled}
                />
              </FormControl>
              <FormField
                control={form.control}
                name={unitName}
                render={({ field: unit }) => (
                  <Select
                    value={typeof unit.value === 'string' ? unit.value : 'hours'}
                    onValueChange={unit.onChange}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-32" aria-label={t('stage.unitOf', { label })}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_UNITS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`unit.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {hint ? (
              <p id={hintId} className="text-muted-foreground text-sm">
                {hint}
              </p>
            ) : null}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
